import React, { startTransition, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Divider, Stack, Typography, Tooltip, TextField, Checkbox, ListItemText, MenuItem, Select } from "@mui/material";
import { DataGrid, GridCellModes } from "@mui/x-data-grid";
import { discountGroupObjects, master_general_information_fields } from "../data";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { GlobalState } from "context/GlobalContext";
import BarcodeInputDialog from "./BarcodeInputDialog";


const schemeTypeField = master_general_information_fields.find((field) => field.key === "scheme_type");
const schemeTypeOptions = schemeTypeField?.option_data?.list || [];

const normalizeToArray = (value) => {
	if (Array.isArray(value)) return value;
	if (value === null || value === undefined || value === "") return [];
	return [value];
};

const buildProductLabel = (row, index) => {
	return [row.category, row.brand, row.option].filter(Boolean).join(" - ") || row.product || row.product_type || String(row.id ?? index + 1);
};

const sanitizeIdPart = (value) => String(value || "scheme_type").replace(/[^A-Za-z0-9_@%]/g, "_");

const DEFAULT_FIELDS = ["store_grade", "store"];

const getEditableFieldsForScheme = (schemeType) => {
	if (schemeType === "NOD") return [...DEFAULT_FIELDS];
	if (!schemeType) return [...DEFAULT_FIELDS,"buy", "get", "value", "percent", "product"];
	if (schemeType === "Flat_Discount_%") return [...DEFAULT_FIELDS,"percent"];
	if (schemeType === "Flat_Discount_Amount" || schemeType === "Flat_Discount_Value") return [...DEFAULT_FIELDS,"get"];
	if (schemeType.includes("%")) return [...DEFAULT_FIELDS,"buy", "percent"];
	if (schemeType.includes("Quantity_Free")) return [...DEFAULT_FIELDS,"buy", "get"];
	if (schemeType.includes("Amount_Off")) return [...DEFAULT_FIELDS,"buy", "get"];
	if (schemeType.includes("Get_certain_amount_off") && schemeType.includes("product_@_Amount")) return [...DEFAULT_FIELDS,"buy", "product","get", "value"];
	if (schemeType.includes("product_@_Amount") || schemeType.includes("product @ Amount")) return [...DEFAULT_FIELDS,"buy", "product", "value"];
	return [...DEFAULT_FIELDS,"buy", "get"];
};

const normalizeRowForScheme = (row) => {
	const editableFields = getEditableFieldsForScheme(row.scheme_type);
	const cleanedRow = { ...row };

	["buy", "get", "value", "percent"].forEach((field) => {
		if (!editableFields.includes(field)) cleanedRow[field] = 0;
	});

	if (!editableFields.includes("product")) cleanedRow.product = "";

	return cleanedRow;
};

const MultiSelectEditCell = ({
	id,
	field,
	value = [],
	api,
	options = [],
}) => {

	const [searchText, setSearchText] = React.useState("");

	const SELECT_ALL = "__ALL__";

	const filteredOptions = options.filter((option) =>
		option
			?.toString()
			?.toLowerCase()
			.includes(searchText.toLowerCase())
	);

	const allSelected =
		filteredOptions.length > 0 &&
		filteredOptions.every((option) =>
			value.includes(option)
		);

	const handleChange = (event) => {

		let selectedValues = event.target.value;

		// SELECT ALL LOGIC
		if (selectedValues.includes(SELECT_ALL)) {

			selectedValues = allSelected
				? value.filter(
						(v) => !filteredOptions.includes(v)
				  )
				: [
						...new Set([
							...value,
							...filteredOptions,
						]),
				  ];
		}

		selectedValues = selectedValues.filter(
			(item) => item !== SELECT_ALL
		);

		api.setEditCellValue({
			id,
			field,
			value: selectedValues,
		});
	};

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				alignItems: "center",
			}}
		>
			<Select
				multiple
				fullWidth
				size="small"
				value={value || []}
				onChange={handleChange}
				displayEmpty
				autoFocus
				MenuProps={{
					PaperProps: {
						style: {
							maxHeight: 350,
							width: 230,
						},
					},
					MenuListProps: {
						dense: true,
					},
				}}

				renderValue={(selected) =>
					selected?.length
						? `${selected.length} Selected`
						: "Select"
				}

				sx={{
					width: "100%",
					backgroundColor: "#fff",
					minWidth: 200,

					"& .MuiSelect-select": {
						paddingTop: "6px",
						paddingBottom: "6px",
						fontSize: "13px",
						display: "flex",
						alignItems: "center",
					},
				}}
			>

				{/* SEARCH */}
				<Box
					sx={{
						p: 1,
						position: "sticky",
						top: 0,
						background: "#fff",
						zIndex: 1,
						borderBottom: "1px solid #eee",
					}}
				>
					<TextField
						fullWidth
						size="small"
						placeholder="Search..."
						value={searchText}
						onChange={(e) =>
							setSearchText(e.target.value)
						}
						onKeyDown={(e) =>
							e.stopPropagation()
						}
					/>
				</Box>

				{/* SELECT ALL */}
				<MenuItem
					value={SELECT_ALL}
					sx={{
						minHeight: 36,
						display: "flex",
						alignItems: "center",
						columnGap: 1,
						overflow: "hidden",
					}}
				>
					<Checkbox
						size="small"
						checked={allSelected}
						sx={{
							padding: "2px",
							margin: 0,
						}}
					/>

					<Box
						sx={{
							fontSize: "13px",
							lineHeight: "18px",
							whiteSpace: "nowrap",
						}}
					>
						Select All
					</Box>
				</MenuItem>

				{/* OPTIONS */}
				{filteredOptions.map((option) => (
					<MenuItem
						key={option}
						value={option}
						sx={{
							minHeight: 36,
							display: "flex",
							alignItems: "center",
							columnGap: 1,
							overflow: "hidden",
						}}
					>
						<Checkbox
							size="small"
							checked={value.includes(option)}
							sx={{
								padding: "2px",
								margin: 0,
							}}
						/>

						<Tooltip
							title={option}
							placement="top"
							arrow
						>
							<Box
								sx={{
									flex: 1,
									fontSize: "13px",
									lineHeight: "18px",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{option}
							</Box>
						</Tooltip>
					</MenuItem>
				))}
			</Select>
		</Box>
	);
};

const ProductEditCell = ({
	id,
	field,
	value = "",
	api,
	options = [],
}) => {
	const normalizedOptions = options
		.map((option) => option?.value || option)
		.filter(Boolean)
		.map((option) => String(option).toUpperCase());
	const datalistId = `product-options-${id}`;

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				px: 0.5,
				py: 0.25,
				backgroundColor: "#fff",
			}}
		>
			<TextField
				fullWidth
				autoFocus
				size="small"
				placeholder="Select or type product"
				value={String(value || "").toUpperCase()}
				onChange={(event) => {
					api.setEditCellValue({
						id,
						field,
						value: String(event.target.value || "").toUpperCase(),
					});
				}}
				InputProps={{
					sx: {
						height: 36,
						fontSize: "13px",
						alignItems: "center",
						backgroundColor: "#fff",
						borderRadius: 1,
						"& input": {
							padding: "8px 10px",
							lineHeight: "20px",
							textTransform: "uppercase",
							boxSizing: "border-box",
						},
					},
				}}
				inputProps={{
					list: datalistId,
					autoComplete: "off",
				}}
			/>
			<datalist id={datalistId}>
				{normalizedOptions.map((option) => (
					<option key={option} value={option} />
				))}
			</datalist>
		</Box>
	);
};

function DiscountValueSection({ value, setValue, disable, handleFinalSummary, setFinalSummary, storeDetails }) {
	const discountKey = discountGroupObjects.discount_value.key;
	const [products, setProducts] = useState([]);
	const generalKey = discountGroupObjects.general_information.key;
	const productKey = discountGroupObjects.product_selection.key;
	const selectedSchemeTypes = normalizeToArray(value[generalKey]?.scheme_type);
	const discountSchemeTypes = selectedSchemeTypes;
	// const discountSchemeTypes = selectedSchemeTypes.filter((schemeType) => schemeType !== "NOD");
	const selectedSchemeTypesKey = selectedSchemeTypes.join("|");
	const discountSchemeTypesKey = discountSchemeTypes.join("|");
	const discountRows = value[discountKey] || [];
	const [discounts, setDiscounts] = useState(discountRows);
	const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [cellModesModel, setCellModesModel] = useState({});

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	const handleCellClick = (params) => {

		if (!params.isEditable) return;

		setCellModesModel((prev) => ({
			...prev,
			[params.id]: {
				...prev[params.id],
				[params.field]: {
					mode: GridCellModes.Edit,
				},
			},
		}));
	};

	const getProducts = async () => {
				try {
					// setDownloading(true);
					// setLoadingOpen(true);
					await pythonVmInstance({
						url: `/discount-master/products`,
						headers: {
							"Content-Type": "application/json",
						},
						method: "GET",
					}).then((res) => {
						setProducts(res?.data);
						// discountData === undefined && setLoadingOpen(false);
					});
				} catch (error) {
					// setSnackBarOpen(true);
					// setLoadingOpen(false);
					setSnackBar({
						...snackBar,
						message: "Failed to download file",
						severity: "error",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
				}
			};

	useEffect(() => {
		setDiscounts(discountRows);
	}, [discountRows]);

	useEffect(() => {
		getProducts();
	}, []);

	useEffect(() => {
		setValue((prev) => {
			const selectedTypes = normalizeToArray(prev[generalKey]?.scheme_type);
			const discountTypes = selectedTypes.filter((schemeType) => schemeType !== "NOD");
			const currentRows = prev[discountKey] || [];
			const updatedRows = currentRows.filter((row) => !row.scheme_type || discountTypes.includes(row.scheme_type) || row.scheme_type === "NOD");

			if (updatedRows.length === currentRows.length) return prev;

			setDiscounts(updatedRows);
			setFinalSummary([]);

			return {
				...prev,
				[discountKey]: updatedRows,
			};
		});
	}, [selectedSchemeTypesKey, discountKey, generalKey, setFinalSummary, setValue]);

	const schemeTypeValueOptions = useMemo(
		() => {
			const options = schemeTypeOptions
				.filter((option) => discountSchemeTypes.includes(option.key))
				.map((option) => ({
					value: option.key,
					label: option.name,
				}));

			return options;
		},
		[discountSchemeTypesKey, selectedSchemeTypes]
	);

	const productValueOptions = useMemo(
		() =>
			(products || []).map((row, index) => ({
				value: row.DEPARTMENT,
				label: row.DEPARTMENT,
			})),
		[products]
	);

	const getNextRowId = (schemeType, rows) => {
		const idPrefix = sanitizeIdPart(schemeType);
		const nextNumber =
			rows.reduce((max, row) => {
				const match = String(row.id || "").match(new RegExp(`^${idPrefix}_(\\d+)$`));
				return match ? Math.max(max, Number(match[1])) : max;
			}, 0) + 1;

		return `${idPrefix}_${nextNumber}`;
	};

	const selectedStoreNames = useMemo(
		() => value?.store_availability?.store || [],
		[value?.store_availability?.store]
	);

	const selectedStores = useMemo(
		() => storeDetails.filter((store) => selectedStoreNames.includes(store.store_name)),
		[storeDetails, selectedStoreNames]
	);

	const allStoreGrades = useMemo(
		() => [...new Set(selectedStores.map((row) => row.store_grade).filter(Boolean))],
		[selectedStores]
	);

	const allStores = useMemo(
		() => [...new Set(selectedStores.map((row) => row.store_name).filter(Boolean))],
		[selectedStores]
	);

	const handleAddRow = useCallback(() => {

		const schemeType = selectedSchemeTypes.includes("NOD")
			? "NOD"
			: (discountSchemeTypes[0] || "");

		const newRow = normalizeRowForScheme({
			id: getNextRowId(
				schemeType,
				discounts
			),

			scheme_type: schemeType,

			product:
				productValueOptions?.[0]?.value || "",

			buy: 0,
			get: 0,
			percent: 0,
			value: 0,

			// DEFAULT SELECT ALL
			store_grade: allStoreGrades,
			store: allStores,
		});

		const updated = [...discounts, newRow];
		startTransition(() => {
			setValue((prev) => ({
				...prev,
				[discountKey]: updated,
			}));
			setDiscounts(updated);
		});
	}, [allStoreGrades, allStores, discountKey, discountSchemeTypes, discounts, productValueOptions, selectedSchemeTypes, setValue]);

	const handleDeleteRow = useCallback((idToDelete) => {
		const updated = discounts.filter((row) => row.id !== idToDelete);
		startTransition(() => {
			setDiscounts(updated);
			setValue((prev) => ({ ...prev, [discountKey]: updated }));
		});
	}, [discountKey, discounts, setValue]);

	const handleRowUpdate = useCallback((newRow, oldRow) => {

		const rowSchemeChanged =
			oldRow.scheme_type !== newRow.scheme_type;

		const productChanged =
			oldRow.product !== newRow.product;

		const storeGradeChanged =
			JSON.stringify(oldRow.store_grade || []) !==
			JSON.stringify(newRow.store_grade || []);

		let updatedRow = normalizeRowForScheme(
			rowSchemeChanged
				? {
						...newRow,
						id: getNextRowId(
							newRow.scheme_type,
							discounts.filter(
								(row) => row.id !== oldRow.id
							)
						),
				}
				: newRow
		);

		// STORE GRADE CHANGED
		if (storeGradeChanged) {

			const selectedGrades =
				newRow.store_grade || [];

			// IF NO STORE GRADE SELECTED
			// CLEAR ALL STORES
			if (selectedGrades.length === 0) {

				updatedRow.store = [];

			} else {

				// FILTER STORES BASED ON SELECTED GRADES
				const filteredStores =
					selectedStores.filter((store) =>
						selectedGrades.includes(
							store.store_grade
						)
					);

				// VALID STORE OPTIONS
				const validStores = [
					...new Set(
						filteredStores
							.map((row) => row.store_name)
							.filter(Boolean)
					),
				];

				// KEEP ONLY VALID STORES
				updatedRow.store = (
					newRow.store || []
				).filter((storeName) =>
					validStores.includes(storeName)
				);

				// IF NO VALID STORES
				if (!updatedRow.store.length) {
					updatedRow.store = [];
				}
			}
		}

		const updatedRows = discounts.map((row) =>
			row.id === oldRow.id
				? updatedRow
				: row
		);

		startTransition(() => {
			setDiscounts(updatedRows);
			setValue((prev) => ({
				...prev,
				[discountKey]: updatedRows,
			}));
		});

		// PRODUCT CHANGED
		if (productChanged && newRow.product) {

			setSelectedProduct({
				rowId: updatedRow.id,
				productName: updatedRow.product,
			});

			setBarcodeDialogOpen(true);
		}

		return updatedRow;
	}, [discountKey, discounts, selectedStores, setValue]);

	const columns = useMemo(() => [
	{
		field: "scheme_type",
		headerName: "Scheme Type",
		editable: true,
		type: "singleSelect",
		valueOptions: schemeTypeValueOptions,
		align: "center",
		headerAlign: "center",
		flex: 2,
		minWidth: 100,
	},

	{
		field: "buy",
		headerName: "BUY",
		editable: true,
		type: "number",
		align: "center",
		headerAlign: "center",
		flex: 1,
		minWidth: 80,
	},

	{
		field: "get",
		headerName: "GET",
		editable: true,
		type: "number",
		align: "center",
		headerAlign: "center",
		flex: 1,
		minWidth: 80,
	},

	{
		field: "percent",
		headerName: "Percent",
		editable: true,
		type: "number",
		align: "center",
		headerAlign: "center",
		flex: 1,
		minWidth: 80,
	},

	{
		field: "product",
		headerName: "Product",
		editable: true,
		align: "center",
		headerAlign: "center",
		flex: 1.6,
		minWidth: 180,
		renderCell: (params) => (
			<Box
				sx={{
					width: "100%",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					fontSize: "13px",
					lineHeight: "20px",
					textTransform: "uppercase",
				}}
			>
				{String(params.value || "").toUpperCase()}
			</Box>
		),
		renderEditCell: (params) => (
			<ProductEditCell
				{...params}
				options={productValueOptions}
			/>
		),
	},

	{
		field: "value",
		headerName: "Value",
		editable: true,
		type: "number",
		align: "center",
		headerAlign: "center",
		flex: 1,
		minWidth: 80,
	},

	{
		field: "barcodes",
		headerName: "Barcodes",
		sortable: false,
		filterable: false,
		renderCell: (params) => (
			<Button
				variant="outlined"
				size="small"
				onClick={() => {
					setSelectedProduct({
						rowId: params.row.id,
						productName: params.row.product,
					});
					setBarcodeDialogOpen(true);
				}}
				disabled={!params.row.product}
			>
				{params.row.barcodes?.length || 0} Barcodes
			</Button>
		),
		width: 130,
		align: "center",
		headerAlign: "center",
	},

	// STORE GRADE
	{
		field: "store_grade",
		headerName: "Store Grade",
		editable: true,
		flex: 1.5,
		minWidth: 220,

		renderCell: (params) =>
			Array.isArray(params.value)
				? params.value.join(", ")
				: "",

		renderEditCell: (params) => {

			const gradeOptions = [
				...new Set(
					selectedStores
						.map((row) => row.store_grade)
						.filter(Boolean)
				),
			].sort();

			return (
				<MultiSelectEditCell
					{...params}
					options={gradeOptions}
				/>
			);
		},

		sortable: false,
		filterable: false,
	},

	// STORE
	{
		field: "store",
		headerName: "Store",
		editable: true,
		flex: 2,
		minWidth: 280,

		renderCell: (params) =>
			Array.isArray(params.value)
				? params.value.join(", ")
				: "",

		renderEditCell: (params) => {

			// SELECTED STORE GRADES
			const selectedGrades =
				params.row.store_grade || [];

			// FILTER STORES BASED ON SELECTED GRADES
			const filteredStores =
				selectedGrades.length > 0
					? selectedStores.filter((store) =>
							selectedGrades.includes(
								store.store_grade
							)
					  )
					: selectedStores;

			// UNIQUE STORE NAMES
			const storeOptions = [
				...new Set(
					filteredStores
						.map((row) => row.store_name)
						.filter(Boolean)
				),
			].sort();

			return (
				<MultiSelectEditCell
					{...params}
					options={storeOptions}
				/>
			);
		},

		sortable: false,
		filterable: false,
	},

	{
		field: "actions",
		headerName: "Actions",
		sortable: false,
		filterable: false,
		renderCell: (params) => (
			<Button
				variant="outlined"
				color="error"
				size="small"
				onClick={() =>
					handleDeleteRow(params.row.id)
				}
			>
				Delete
			</Button>
		),
		width: 100,
		align: "center",
		headerAlign: "center",
	},
], [disable, discountSchemeTypes.length, handleDeleteRow, productValueOptions, schemeTypeValueOptions, selectedSchemeTypes, selectedStores]);

	const handleSaveBarcodes = useCallback((barcodes) => {
		if (!selectedProduct) return;

		const updatedRows = discounts.map((row) => {
			if (row.id === selectedProduct.rowId) {
				return { ...row, barcodes: barcodes };
			}
			return row;
		});

		startTransition(() => {
			setDiscounts(updatedRows);
			setValue((prev) => ({
				...prev,
				[discountKey]: updatedRows,
			}));
		});
	}, [discountKey, discounts, selectedProduct, setValue]);

	const isCellEditable = useCallback((params) => {
		if (disable) return false;
		if (params.field === "scheme_type") return true;
		return getEditableFieldsForScheme(params.row.scheme_type).includes(params.field);
	}, [disable]);

	const renderContent = () => {
		// if (discountSchemeTypes.length === 0) {
		// 	return (
		// 		<Typography variant="body2" color="text.secondary">
		// 			Select scheme type in General Information to add discount values.
		// 		</Typography>
		// 	);
		// }

		return (
			<>
				<Stack direction="row" justifyContent="flex-end" alignItems="center" mb={1} columnGap={2}>
					<Button variant="contained" onClick={handleAddRow} disabled={disable || (discountSchemeTypes.length === 0 && !selectedSchemeTypes.includes("NOD"))}>
						Add Row
					</Button>
				</Stack>
				<Divider sx={{ mb: 2 }} />
				<Stack minHeight={220} maxHeight={250}>
					<DataGrid
						rows={discounts}
						columns={columns}
						rowHeight={46}
						ellModesModel={cellModesModel}
						onCellModesModelChange={setCellModesModel}
						onCellClick={handleCellClick}
						hideFooter
						disableColumnMenu
						processRowUpdate={handleRowUpdate}
						isCellEditable={isCellEditable}
						experimentalFeatures={{ newEditingApi: true }}
						getCellClassName={(params) =>
							params.field !== "actions" && !isCellEditable(params) ? "discount-value-disabled-cell" : ""
						}
						sx={{
							"& .MuiDataGrid-cell": {
								display: "flex",
								alignItems: "center",
								paddingY: "4px",
							},
							"& .MuiDataGrid-cell--editing": {
								padding: "2px !important",
								display: "flex",
								alignItems: "center",
								backgroundColor: "#fff",
								boxShadow: "none",
							},
							"& .MuiDataGrid-cell--editing:focus-within": {
								outline: "none",
							},
							"& .discount-value-disabled-cell": {
								backgroundColor: "#f5f5f5",
								color: "text.disabled",
							},
						}}
					/>
				</Stack>
			</>
		);
	};

	return (
		<Box width="95%" p={"1, 2"}>
			{renderContent()}
			<BarcodeInputDialog
				open={barcodeDialogOpen}
				onClose={() => {
					setBarcodeDialogOpen(false);
					setSelectedProduct(null);
				}}
				onSave={handleSaveBarcodes}
				productName={selectedProduct?.productName || ""}
				existingBarcodes={selectedProduct ? discounts.find(row => row.id === selectedProduct.rowId)?.barcodes || [] : []}
			/>
		</Box>
	);
}

export default DiscountValueSection;
